import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, StickyNote, Plus, CalendarClock, X, Trash2 } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { applicationService } from '@core/supabase/services/applicationService';
import { emailService } from '@core/supabase/services/emailService';
import { calendarNotesService } from '@core/supabase/services/calendarNotesService';
import {
  calendarEventsService,
  type CalendarEventRow,
} from '@core/supabase/services/calendarEventsService';
import { ApplicationStatus } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';

interface FeedEvent {
  id: string;
  date: string;
  title: string;
  sub: string;
  type: 'interview' | 'followup';
  applicationId: string;
}

const INTERVIEW_STATUSES: ApplicationStatus[] = [
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
];

const LS_KEY = 'ggj_b2b_calendar_notes';

const REMINDER_OPTIONS: { minutes: number; label: string }[] = [
  { minutes: 0, label: 'At start' },
  { minutes: 10, label: '10 min before' },
  { minutes: 15, label: '15 min before' },
  { minutes: 30, label: '30 min before' },
  { minutes: 60, label: '1 hour before' },
  { minutes: 120, label: '2 hours before' },
  { minutes: 1440, label: '1 day before' },
];

function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local calendar dates an event spans (inclusive). */
function localDateKeysBetween(startIso: string, endIso: string): string[] {
  const start = new Date(startIso);
  const end = new Date(endIso);
  const keys: string[] = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  while (cur <= last) {
    keys.push(toYmd(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return keys;
}

function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day}T${h}:${min}`;
}

function localDayBounds(dateStr: string): { starts_at: string; ends_at: string } {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const start = new Date(y, mo - 1, d, 0, 0, 0, 0);
  const end = new Date(y, mo - 1, d, 23, 59, 59, 999);
  return { starts_at: start.toISOString(), ends_at: end.toISOString() };
}

type ModalState =
  | null
  | { kind: 'new'; defaultDate: string }
  | { kind: 'edit'; event: CalendarEventRow };

export function CalendarNotesPage() {
  const { user } = useAuth();
  const orgId = user?.orgId ?? null;
  const userId = user?.id ?? null;

  const [month, setMonth] = useState(() => new Date());
  const [feedEvents, setFeedEvents] = useState<FeedEvent[]>([]);
  const [userEvents, setUserEvents] = useState<CalendarEventRow[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [newNoteDate, setNewNoteDate] = useState('');
  const [newNoteText, setNewNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveQueueRef = useRef<Record<string, string>>({});
  const [modal, setModal] = useState<ModalState>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const yearMonth = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;

  const userEventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEventRow[]>();
    for (const ev of userEvents) {
      for (const key of localDateKeysBetween(ev.starts_at, ev.ends_at)) {
        const arr = map.get(key) ?? [];
        if (!arr.some((e) => e.id === ev.id)) arr.push(ev);
        map.set(key, arr);
      }
    }
    return map;
  }, [userEvents]);

  /** SCH-010: DB + listRecent when in an org; localStorage fallback when not. */
  useEffect(() => {
    if (!orgId || !userId) {
      try {
        const s = localStorage.getItem(LS_KEY);
        setNotes(s ? JSON.parse(s) : {});
      } catch {
        setNotes({});
      }
      return;
    }
    setNotesLoading(true);
    Promise.all([
      calendarNotesService.listRecent(orgId, userId, 50),
      calendarNotesService.listByMonth(orgId, userId, yearMonth),
    ])
      .then(([recent, monthRows]) => {
        const map: Record<string, string> = {};
        for (const r of recent) map[r.note_date] = r.body;
        for (const r of monthRows) map[r.note_date] = r.body;
        setNotes(map);
      })
      .catch(() => {
        /* keep prior notes */
      })
      .finally(() => setNotesLoading(false));
  }, [orgId, userId, yearMonth]);

  // ATS feed (interviews + follow-ups)
  useEffect(() => {
    if (!orgId) return;
    setLoading(true);
    Promise.all([applicationService.list(orgId), emailService.getUpcomingFollowups(orgId)])
      .then(([apps, followups]) => {
        const out: FeedEvent[] = [];
        apps.forEach((a) => {
          if (INTERVIEW_STATUSES.includes(a.status)) {
            out.push({
              id: `a-${a.id}`,
              date: a.status_updated_at.split('T')[0],
              title: `${a.candidate?.full_name ?? 'Candidate'} — ${a.job?.title ?? 'Role'}`,
              sub: a.client?.name ?? '',
              type: 'interview',
              applicationId: a.id,
            });
          }
        });
        followups.forEach((f) => {
          out.push({
            id: `f-${f.id}`,
            date: f.next_followup_at.split('T')[0],
            title: f.subject,
            sub: f.recipient_name ?? '',
            type: 'followup',
            applicationId: f.application_id,
          });
        });
        setFeedEvents(out);
      })
      .finally(() => setLoading(false));
  }, [orgId]);

  // User calendar events (PRD §9.9)
  useEffect(() => {
    if (!orgId || !userId) {
      setUserEvents([]);
      return;
    }
    const y = month.getFullYear();
    const m = month.getMonth();
    const rangeStart = new Date(y, m, 1, 0, 0, 0, 0).toISOString();
    const rangeEnd = new Date(y, m + 1, 0, 23, 59, 59, 999).toISOString();
    setEventsLoading(true);
    calendarEventsService
      .listInRange(orgId, userId, rangeStart, rangeEnd)
      .then(setUserEvents)
      .catch(() => setUserEvents([]))
      .finally(() => setEventsLoading(false));
  }, [orgId, userId, month]);

  const persistNote = useCallback(
    (date: string, body: string) => {
      if (!orgId || !userId) {
        setNotes((prev) => {
          const next = { ...prev, [date]: body };
          try {
            localStorage.setItem(LS_KEY, JSON.stringify(next));
          } catch {
            /* ignore */
          }
          return next;
        });
        return;
      }
      setNotes((prev) => ({ ...prev, [date]: body }));
      saveQueueRef.current[date] = body;

      const flush = async () => {
        const queue = { ...saveQueueRef.current };
        saveQueueRef.current = {};
        setSaving(true);
        try {
          await Promise.all(Object.entries(queue).map(([d, b]) => calendarNotesService.upsert(orgId, userId, d, b)));
        } catch {
          /* ignore */
        }
        setSaving(false);
      };
      window.clearTimeout((persistNote as unknown as { _t?: number })._t);
      (persistNote as unknown as { _t?: number })._t = window.setTimeout(flush, 600);
    },
    [orgId, userId]
  );

  const saveNote = (key: string, value: string) => {
    persistNote(key, value);
    setEditingNote(null);
  };

  const reloadUserEvents = useCallback(() => {
    if (!orgId || !userId) return;
    const y = month.getFullYear();
    const m = month.getMonth();
    const rangeStart = new Date(y, m, 1, 0, 0, 0, 0).toISOString();
    const rangeEnd = new Date(y, m + 1, 0, 23, 59, 59, 999).toISOString();
    calendarEventsService.listInRange(orgId, userId, rangeStart, rangeEnd).then(setUserEvents);
  }, [orgId, userId, month]);

  const year = month.getFullYear();
  const m = month.getMonth();
  const firstDay = new Date(year, m, 1);
  const lastDay = new Date(year, m + 1, 0);
  const startPad = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const weeks: (number | null)[][] = [];
  let week: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) week.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push(d);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  while (week.length < 7) week.push(null);
  if (week.some(Boolean)) weeks.push(week);

  const fmt = (d: number) => `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const today = new Date();
  const storageHint = orgId
    ? notesLoading
      ? 'Loading notes…'
      : saving
        ? 'Saving…'
        : 'Notes saved to your account in this organisation (not visible to other users).'
    : 'Notes stored in this browser only (join an org to persist)';

  const selectedDayEvents = selectedDay ? (userEventsByDate.get(selectedDay) ?? []) : [];
  const selectedDayFeed = selectedDay ? feedEvents.filter((e) => e.date === selectedDay) : [];

  return (
    <div>
      <PageHeader
        icon={CalendarClock}
        breadcrumb={[{ label: 'Pipeline', href: '/calendar-notes' }, { label: 'Calendar & notes' }]}
        title="Calendar & notes"
        subtitle="Create events on any day. Reminders in the bell menu; interview and follow-up rows from the ATS are read-only here."
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('calendar_notes')}
            onExport={(fmt, scope) => listExportNotImplemented('Calendar & notes', fmt, scope)}
          />
        }
        action={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {orgId && userId && (
            <button
              type="button"
              onClick={() => {
                const d = selectedDay ?? toYmd(new Date());
                setModal({ kind: 'new', defaultDate: d });
              }}
              className="btn-primary text-xs inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" /> New Event
            </button>
          )}
          <button
            type="button"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1))}
            className="p-1.5 vscode-text-tertiary hover:vscode-text-primary"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="vscode-text-secondary min-w-[160px] text-center text-sm">
            {month.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </span>
          <button
            type="button"
            onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1))}
            className="p-1.5 vscode-text-tertiary hover:vscode-text-primary"
            aria-label="Next month"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        }
      />

      {loading ? (
        <div className="card animate-pulse h-96" />
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {eventsLoading && (
              <p className="text-xs vscode-text-tertiary">Refreshing your events…</p>
            )}
            <div className="card overflow-hidden p-0">
              <div className="grid grid-cols-7 text-xs vscode-text-tertiary border-b vscode-border">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((w) => (
                  <div key={w} className="p-2 text-center font-medium">
                    [{w}]
                  </div>
                ))}
              </div>
              {weeks.map((wk, wi) => (
                <div key={wi} className="grid grid-cols-7 border-b vscode-border last:border-0">
                  {wk.map((d, di) => {
                    if (d === null) return <div key={di} className="min-h-[96px] vscode-bg-tertiary/40" />;
                    const key = fmt(d);
                    const dayFeed = feedEvents.filter((e) => e.date === key);
                    const dayUser = userEventsByDate.get(key) ?? [];
                    const hasNote = !!notes[key];
                    const isTodayDate =
                      year === today.getFullYear() && m === today.getMonth() && d === today.getDate();
                    const isSelected = selectedDay === key;
                    return (
                      <button
                        type="button"
                        key={di}
                        onClick={() => setSelectedDay(key)}
                        className={`min-h-[96px] p-1 text-left border-r vscode-border last:border-r-0 transition-colors hover:vscode-bg-hover ${
                          isTodayDate ? 'bg-brand-600/10' : ''
                        } ${isSelected ? 'ring-2 ring-inset ring-brand-500/50' : ''}`}
                      >
                        <span
                          className={`text-xs font-semibold ${isTodayDate ? 'text-brand-400' : 'vscode-text-secondary'}`}
                        >
                          {d}
                        </span>
                        <div className="space-y-0.5 mt-1">
                          {dayUser.slice(0, 3).map((ev) => (
                            <div
                              key={ev.id}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setModal({ kind: 'edit', event: ev });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.stopPropagation();
                                  setModal({ kind: 'edit', event: ev });
                                }
                              }}
                              className="block w-full truncate rounded px-1 py-0.5 text-left text-[10px] bg-emerald-900/45 text-emerald-200 cursor-pointer hover:bg-emerald-800/50"
                            >
                              {ev.title}
                            </div>
                          ))}
                          {dayFeed.slice(0, Math.max(0, 3 - dayUser.length)).map((e) => (
                            <Link
                              key={e.id}
                              to={`/applications/${e.applicationId}`}
                              onClick={(ev) => ev.stopPropagation()}
                              className={`block px-1 py-0.5 rounded text-[10px] truncate ${
                                e.type === 'followup'
                                  ? 'bg-amber-900/40 text-amber-200'
                                  : 'bg-brand-900/40 text-brand-200'
                              }`}
                            >
                              {e.title}
                            </Link>
                          ))}
                          {hasNote && (
                            <span
                              className="block px-1 py-0.5 rounded text-[10px] truncate bg-amber-900/25 text-amber-300/90"
                              title={notes[key]}
                            >
                              <StickyNote className="inline w-2.5 h-2.5 mr-0.5 align-middle" />
                              {notes[key].slice(0, 18)}
                              {notes[key].length > 18 ? '…' : ''}
                            </span>
                          )}
                          {dayUser.length + dayFeed.length > 3 && (
                            <span className="text-[9px] vscode-text-tertiary pl-1">
                              +{dayUser.length + dayFeed.length - 3} more
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="card">
              <h2 className="text-sm font-semibold vscode-text-primary mb-1 flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-brand-400" />
                {selectedDay ? `Day — ${selectedDay}` : 'Pick a day'}
              </h2>
              <p className="text-xs vscode-text-tertiary mb-3">
                Click a day on the grid to see items. Your events are editable; pipeline items open the application.
              </p>
              {selectedDay && (
                <ul className="space-y-2 text-xs max-h-56 overflow-y-auto">
                  {selectedDayEvents.length === 0 && selectedDayFeed.length === 0 && !notes[selectedDay] && (
                    <li className="vscode-text-tertiary">Nothing on this day — add an event or note.</li>
                  )}
                  {selectedDayEvents.map((ev) => (
                    <li key={ev.id}>
                      <button
                        type="button"
                        onClick={() => setModal({ kind: 'edit', event: ev })}
                        className="w-full text-left rounded-lg border vscode-border vscode-bg-tertiary/50 px-2 py-2 hover:vscode-bg-hover"
                      >
                        <span className="font-medium vscode-text-primary block">{ev.title}</span>
                        <span className="vscode-text-tertiary">
                          {ev.all_day
                            ? 'All day'
                            : `${new Date(ev.starts_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${new Date(ev.ends_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                        </span>
                      </button>
                    </li>
                  ))}
                  {selectedDayFeed.map((e) => (
                    <li key={e.id}>
                      <Link
                        to={`/applications/${e.applicationId}`}
                        className="block rounded-lg border vscode-border px-2 py-2 hover:vscode-bg-hover"
                      >
                        <span className="font-medium vscode-text-primary">{e.title}</span>
                        <span className="vscode-text-tertiary block capitalize">{e.type}</span>
                      </Link>
                    </li>
                  ))}
                  {notes[selectedDay]?.trim() && (
                    <li className="rounded-lg border border-amber-800/40 bg-amber-950/20 px-2 py-2 vscode-text-secondary">
                      <span className="text-[10px] uppercase vscode-text-tertiary">Note</span>
                      <p className="mt-0.5">{notes[selectedDay]}</p>
                      <button
                        type="button"
                        className="mt-1 text-[11px] text-brand-400"
                        onClick={() => {
                          setNewNoteDate(selectedDay);
                          setEditingNote(selectedDay);
                        }}
                      >
                        Edit in notes panel →
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="card">
              <h2 className="text-sm font-semibold vscode-text-primary mb-1 flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-brand-400" /> Notes
              </h2>
              <p className="text-xs vscode-text-tertiary mb-3">{storageHint}</p>
              <div className="flex gap-2 mb-3 flex-wrap">
                <input
                  type="date"
                  className="input flex-1 min-w-[140px] text-xs"
                  value={newNoteDate}
                  onChange={(e) => setNewNoteDate(e.target.value)}
                />
                <input
                  type="text"
                  className="input flex-1 min-w-[120px] text-xs"
                  placeholder="Note…"
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newNoteDate) {
                      persistNote(newNoteDate, newNoteText);
                      setNewNoteDate('');
                      setNewNoteText('');
                    }
                  }}
                />
                <button
                  type="button"
                  className="btn-primary py-1 px-2 text-xs"
                  onClick={() => {
                    if (newNoteDate) {
                      persistNote(newNoteDate, newNoteText);
                      setNewNoteDate('');
                      setNewNoteText('');
                    }
                  }}
                >
                  [Add Note]
                </button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {Object.entries(notes)
                  .filter(([, v]) => v.trim())
                  .sort(([a], [b]) => b.localeCompare(a))
                  .slice(0, 20)
                  .map(([date, text]) => (
                    <div key={date} className="p-2 rounded-lg vscode-bg-tertiary/60 text-xs">
                      <p className="vscode-text-tertiary mb-1">{date}</p>
                      {editingNote === date ? (
                        <textarea
                          className="input w-full text-xs min-h-[60px]"
                          value={text}
                          onChange={(e) => setNotes((n) => ({ ...n, [date]: e.target.value }))}
                          onBlur={(e) => saveNote(date, e.target.value)}
                          autoFocus
                        />
                      ) : (
                        <p className="vscode-text-primary cursor-pointer" onClick={() => setEditingNote(date)}>
                          {text}
                        </p>
                      )}
                    </div>
                  ))}
                {Object.values(notes).every((v) => !v.trim()) && (
                  <p className="text-xs vscode-text-tertiary">No notes yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {modal && orgId && userId && (
        <EventModal
          orgId={orgId}
          userId={userId}
          modal={modal}
          onClose={() => setModal(null)}
          onSaved={() => {
            reloadUserEvents();
            setModal(null);
          }}
        />
      )}

    </div>
  );
}

function EventModal({
  orgId,
  userId,
  modal,
  onClose,
  onSaved,
}: {
  orgId: string;
  userId: string;
  modal: Exclude<ModalState, null>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = modal.kind === 'edit';
  const initial = isEdit
    ? modal.event
    : null;

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [location, setLocation] = useState(initial?.location ?? '');
  const [allDay, setAllDay] = useState(initial?.all_day ?? false);
  const [dayDate, setDayDate] = useState(
    isEdit ? toYmd(new Date(initial.starts_at)) : modal.defaultDate
  );
  const [startLocal, setStartLocal] = useState(
    isEdit && !initial.all_day ? toDatetimeLocalValue(initial.starts_at) : `${modal.defaultDate}T09:00`
  );
  const [endLocal, setEndLocal] = useState(
    isEdit && !initial.all_day
      ? toDatetimeLocalValue(initial.ends_at)
      : `${modal.defaultDate}T10:00`
  );
  const [reminderSet, setReminderSet] = useState<Set<number>>(() => {
    if (isEdit && initial.reminder_offsets_minutes?.length) {
      return new Set(initial.reminder_offsets_minutes);
    }
    return new Set([15]);
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const toggleReminder = (minutes: number) => {
    setReminderSet((prev) => {
      const next = new Set(prev);
      if (next.has(minutes)) next.delete(minutes);
      else next.add(minutes);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!title.trim()) {
      setErr('Title is required.');
      return;
    }
    let starts_at: string;
    let ends_at: string;
    if (allDay) {
      const b = localDayBounds(dayDate);
      starts_at = b.starts_at;
      ends_at = b.ends_at;
    } else {
      const s = new Date(startLocal).getTime();
      const en = new Date(endLocal).getTime();
      if (!Number.isFinite(s) || !Number.isFinite(en) || en < s) {
        setErr('End must be after start.');
        return;
      }
      starts_at = new Date(startLocal).toISOString();
      ends_at = new Date(endLocal).toISOString();
    }
    const offsets = Array.from(reminderSet).sort((a, b) => a - b);
    setSaving(true);
    try {
      if (modal.kind === 'edit') {
        await calendarEventsService.update(orgId, userId, modal.event.id, {
          title,
          description: description.trim() || null,
          location: location.trim() || null,
          all_day: allDay,
          starts_at,
          ends_at,
          reminder_offsets_minutes: offsets,
        });
      } else {
        await calendarEventsService.create(orgId, userId, {
          title,
          description: description.trim() || null,
          location: location.trim() || null,
          all_day: allDay,
          starts_at,
          ends_at,
          reminder_offsets_minutes: offsets,
        });
      }
      onSaved();
    } catch {
      setErr('Could not save. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const runDelete = async () => {
    if (modal.kind !== 'edit') return;
    setSaving(true);
    try {
      await calendarEventsService.delete(orgId, userId, modal.event.id);
      onSaved();
    } catch {
      setErr('Could not delete.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <ConfirmDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => {
          setDeleteConfirmOpen(false);
          void runDelete();
        }}
        title="Delete event?"
        message="Delete this event and its reminders?"
        confirmLabel="Delete"
        variant="danger"
        requireTypedConfirm="DELETE"
      />
      <div
        className="w-full max-w-md rounded-xl border vscode-border vscode-bg-elevated shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between gap-2 px-5 py-3 border-b vscode-border"
          style={{ backgroundColor: 'var(--brand-popup-header)' }}
        >
          <div className="flex items-center gap-2 min-w-0 text-white">
            <CalendarClock className="w-5 h-5 shrink-0 opacity-95" />
            <h3 className="text-lg font-semibold truncate">{isEdit ? 'Edit event' : 'New event'}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 text-white/90 hover:text-white rounded-lg shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-5 pt-4">
          <div>
            <label className="label">Title</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </div>
          <div>
            <label className="label">Description (optional)</label>
            <textarea className="input min-h-[72px]" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <label className="label">Location (optional)</label>
            <input className="input" value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 text-sm vscode-text-secondary cursor-pointer">
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} className="rounded border vscode-border" />
            All day
          </label>
          {allDay ? (
            <div>
              <label className="label">Date</label>
              <input type="date" className="input" value={dayDate} onChange={(e) => setDayDate(e.target.value)} required />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Starts</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={startLocal}
                  onChange={(e) => setStartLocal(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="label">Ends</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={endLocal}
                  onChange={(e) => setEndLocal(e.target.value)}
                  required
                />
              </div>
            </div>
          )}
          <div>
            <p className="label mb-1">Reminders</p>
            <p className="text-[11px] vscode-text-tertiary mb-2">
              Shown in the header bell when due. Enable desktop alerts from the bell menu.
            </p>
            <div className="flex flex-wrap gap-2">
              {REMINDER_OPTIONS.map((o) => (
                <label
                  key={o.minutes}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs cursor-pointer ${
                    reminderSet.has(o.minutes)
                      ? 'border-brand-500 bg-brand-600/15 vscode-text-primary'
                      : 'vscode-border vscode-text-secondary'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={reminderSet.has(o.minutes)}
                    onChange={() => toggleReminder(o.minutes)}
                    className="rounded border vscode-border"
                  />
                  {o.label}
                </label>
              ))}
            </div>
          </div>
          {err && <p className="text-sm text-red-400">{err}</p>}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save' : 'Create'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            {isEdit && (
              <button
                type="button"
                className="btn-secondary text-red-400 border-red-800 hover:bg-red-950/40 ml-auto inline-flex items-center gap-1"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={saving}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
